import axios from 'axios';
import { sessionKey } from 'reducer/rAuth';

/**
 * 백엔드 주소. public/config.js 가 정한다.
 *
 * 화면과 백엔드는 다른 주소에 뜬다(예: partner.thinkfree.com / api.partner.thinkfree.com).
 * 주소를 빌드에 박지 않는다 - 환경마다 다시 빌드하면 테스트한 화면과 운영에 올린 화면이
 * 달라진다. config.js 가 없으면 같은 주소의 /api 로 떨어지는데, 정적 호스팅에서는 그 주소가
 * 화면 파일을 돌려주므로 모든 요청이 실패한다. 그래서 콘솔에 남긴다.
 */
const apiUrl = (window.PORTAL_CONFIG?.apiUrl ?? '').replace(/\/+$/, '');
if (!window.PORTAL_CONFIG) {
    console.error('[config] config.js 를 읽지 못했습니다. 백엔드 주소를 알 수 없어 같은 주소의 /api 를 부릅니다.');
}

/**
 * 포탈 API 클라이언트.
 *
 * 다른 주소의 백엔드를 부르므로 백엔드가 이 화면의 주소에 CORS 를 열어 두어야 한다
 * (PORTAL_FRONTEND_URL). withCredentials 는 세션 쿠키를 실어 보내게 한다 - 빼면 요청마다
 * 로그인이 풀린 것처럼 401 이 온다.
 */
const api = axios.create({
    baseURL: `${apiUrl}/api`,
    withCredentials: true,
});

/**
 * 세션이 끊기면 로그인 화면으로 보낸다.
 *
 * 이게 없으면 화면이 빈 목록이나 "알 수 없는 오류" 를 보여주고, 사용자는 로그인이 풀린
 * 것인지 데이터가 없는 것인지 알 수 없다.
 */
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const path = window.location.pathname;
        const isAuthPage = path.startsWith('/login') || path.startsWith('/setup');
        if (error.response?.status === 401 && !isAuthPage) {
            window.localStorage.removeItem(sessionKey);
            // replace 다. 세션이 끊긴 화면을 뒤로가기 스택에 남기지 않는다.
            window.location.replace('/login');
        }
        return Promise.reject(error);
    }
);

/**
 * 파일을 받아 브라우저 다운로드로 넘긴다.
 *
 * 응답 봉투를 쓰지 않는 유일한 경로다 — 본문이 파일 바이트여야 한다. 파일명은
 * `Content-Disposition` 에서 꺼낸다. 한글 파일명이 URL 인코딩되어 오므로 디코드한다.
 */
export async function downloadFile(path, fallbackName) {
    const response = await api.get(path, { responseType: 'blob' });

    const disposition = response.headers['content-disposition'] || '';
    let fileName = fallbackName;
    const match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
        || disposition.match(/filename="?([^";]+)"?/i);
    if (match) {
        fileName = decodeURIComponent(match[1].replace(/\+/g, ' '));
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

/**
 * 요청 식별자.
 *
 * 발급 버튼을 누를 때마다 만들어 보낸다. 발급 서버가 이 값으로 <b>이중 발급을 막는다</b> —
 * 응답이 늦어 사용자가 다시 누르거나 네트워크 오류로 재시도할 때, 같은 값이면 두 번째는
 * 거부되고 첫 발급 건이 돌아온다. 새 값으로 부르면 두 건이 발급된다.
 */
export function newRequestId() {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    // 구형 브라우저 대비. 충돌 확률이 UUID 보다 높지만 없는 것보다 낫다.
    return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default api;
